/**
 * A-ONE mobile header navigation controller.
 * Closes the native <details> hamburger dropdown on outside pointer,
 * Escape, and when leaving the mobile breakpoint.
 */
(function () {
  'use strict';

  /** @type {Window & Record<string, any>} */
  const runtimeWindow = window;
  const INIT_FLAG = '__aoneMobileNavigationScriptInitialized';
  const HEADER_SELECTOR = 'header-component.aone-header';
  const DETAILS_SELECTOR = '[data-aone-mobile-menu]';
  const TOGGLE_SELECTOR = '[data-aone-mobile-menu-toggle]';
  const PANEL_SELECTOR = '[data-aone-mobile-menu-panel]';
  const MOBILE_MEDIA = '(max-width: 749px)';

  if (runtimeWindow[INIT_FLAG]) return;
  runtimeWindow[INIT_FLAG] = true;

  class AoneMobileNavigation {
    /**
     * @param {HTMLElement} header
     */
    constructor(header) {
      this.header = header;
      this.details = header.querySelector(DETAILS_SELECTOR);
      this.toggle = header.querySelector(TOGGLE_SELECTOR);
      this.panel = header.querySelector(PANEL_SELECTOR);
      this.mobileQuery = window.matchMedia(MOBILE_MEDIA);

      this.onToggle = this.onToggle.bind(this);
      this.handleOutsidePointer = this.handleOutsidePointer.bind(this);
      this.handleEscape = this.handleEscape.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
    }

    init() {
      if (!(this.details instanceof HTMLDetailsElement) || !(this.toggle instanceof HTMLElement)) {
        return;
      }

      this.syncA11y();
      this.details.addEventListener('toggle', this.onToggle);
      document.addEventListener('pointerdown', this.handleOutsidePointer);
      document.addEventListener('keydown', this.handleEscape);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onViewportChange);
      } else if (this.mobileQuery.addListener) {
        this.mobileQuery.addListener(this.onViewportChange);
      }
    }

    isOpen() {
      return Boolean(this.details && this.details.hasAttribute('open'));
    }

    isMobileViewport() {
      return this.mobileQuery.matches;
    }

    open() {
      if (!(this.details instanceof HTMLDetailsElement) || !this.isMobileViewport()) return;
      if (this.isOpen()) return;
      this.details.setAttribute('open', '');
      this.syncA11y();
    }

    close() {
      if (!(this.details instanceof HTMLDetailsElement) || !this.isOpen()) return;

      this.details.removeAttribute('open');

      this.details.querySelectorAll('details.aone-header__mobile-sub[open]').forEach((subMenu) => {
        subMenu.removeAttribute('open');
      });

      this.syncA11y();
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    syncA11y() {
      if (!(this.toggle instanceof HTMLElement)) return;

      const open = this.isOpen();
      this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      if (this.panel instanceof HTMLElement) {
        this.panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
    }

    onToggle() {
      this.syncA11y();
    }

    /**
     * @param {PointerEvent | Event} event
     */
    handleOutsidePointer(event) {
      if (!this.isOpen() || !this.isMobileViewport()) return;
      if (!(this.details instanceof HTMLDetailsElement)) return;

      const target = event.target;
      if (!(target instanceof Node)) return;

      // Summary (hamburger) and panel both live inside <details>, so native
      // toggle still works and inside clicks never hit this close path.
      if (this.details.contains(target)) return;

      this.close();
    }

    /**
     * @param {KeyboardEvent} event
     */
    handleEscape(event) {
      if (event.key !== 'Escape' || !this.isOpen()) return;

      this.close();
      this.toggle?.focus();
    }

    onViewportChange() {
      if (!this.isMobileViewport()) {
        this.close();
      }
    }

    destroy() {
      if (this.details instanceof HTMLDetailsElement) {
        this.details.removeEventListener('toggle', this.onToggle);
      }

      document.removeEventListener('pointerdown', this.handleOutsidePointer);
      document.removeEventListener('keydown', this.handleEscape);

      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onViewportChange);
      } else if (this.mobileQuery.removeListener) {
        this.mobileQuery.removeListener(this.onViewportChange);
      }
    }
  }

  /**
   * @param {HTMLElement} header
   */
  function initHeader(header) {
    if (!(header instanceof HTMLElement)) return;

    if (header._aoneMobileNavigation) {
      header._aoneMobileNavigation.destroy();
      header._aoneMobileNavigation = null;
    }

    const controller = new AoneMobileNavigation(header);
    controller.init();
    header._aoneMobileNavigation = controller;
  }

  /**
   * @param {ParentNode | null | undefined} root
   */
  function initAll(root) {
    const scope = root instanceof Document || root instanceof Element ? root : document;
    const headers =
      scope instanceof Element && scope.matches(HEADER_SELECTOR)
        ? [scope]
        : Array.from(scope.querySelectorAll(HEADER_SELECTOR));

    headers.forEach((header) => {
      if (header instanceof HTMLElement) initHeader(header);
    });
  }

  /**
   * @param {Event} event
   */
  function handleSectionLoad(event) {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;
    initAll(target);
  }

  /**
   * @param {Event} event
   */
  function handleSectionUnload(event) {
    const target = /** @type {EventTarget | null} */ (event.target);
    if (!(target instanceof HTMLElement)) return;

    const headers =
      target.matches(HEADER_SELECTOR)
        ? [target]
        : Array.from(target.querySelectorAll(HEADER_SELECTOR));

    headers.forEach((header) => {
      if (!(header instanceof HTMLElement) || !header._aoneMobileNavigation) return;
      header._aoneMobileNavigation.destroy();
      header._aoneMobileNavigation = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);

  runtimeWindow.AoneMobileNavigation = AoneMobileNavigation;
})();
